import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  Mail,
  Phone,
  Briefcase,
  Award,
  UserCheck,
  UserX
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../../services/api';
import type { EmployeeProfile, DailyAttendanceEntry } from '../../types/analytics';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface AttendanceStats {
  totalDays: number;
  presentDays: number;
  absentDays: number;
  leaveDays: number;
  attendancePercent: number;
}

// Transform API profile data to our format
const transformProfileData = (apiData: any, empIdParam: string, kvIdParam: number, passedKvInfo: any): EmployeeProfile => {
  // Extract post/designation - handle both string and object formats
  const getPostName = (postData: any): string => {
    if (!postData) return 'Staff';
    if (typeof postData === 'string') return postData;
    return postData.name || postData.alias || 'Staff';
  };

  return {
    empId: apiData.empId || Number(empIdParam),
    empName: apiData.empName || '',
    empType: apiData.empType === 'contractual' ? 'contractual' : 'regular',
    post: getPostName(apiData.empPost || apiData.post),
    subject: apiData.subject || '',
    gender: apiData.gender === 1 || apiData.empGender === 1 ? 'Male' : 'Female',
    designation: getPostName(apiData.empPost || apiData.designation),
    email: apiData.email || `emp${empIdParam}@kvs.edu.in`,
    phone: apiData.phone || '+91-XXXXXXXXXX',
    dateOfJoining: apiData.dateOfJoining || '',
    qualifications: apiData.qualifications || [],
    currentKvId: apiData.kvId || kvIdParam,
    currentKvName: apiData.kvName || passedKvInfo?.kvName || '',
    currentRoName: apiData.roName || passedKvInfo?.roName || '',
    currentStateName: apiData.stateName || passedKvInfo?.stateName || ''
  };
};

const EmployeeAttendanceDetail: React.FC = () => {
  const { kvId, empId } = useParams<{ kvId: string; empId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get passed state from navigation
  const passedEmployee = location.state?.employee;
  const passedKvInfo = location.state?.kvInfo;
  const passedEmpType = location.state?.empType || searchParams.get('emptype') || 'regular';

  const carouselImages = [img1];

  // State
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [monthlyRegister, setMonthlyRegister] = useState<DailyAttendanceEntry[]>([]);
  const [stats, setStats] = useState<AttendanceStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Calendar navigation
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // Transform attendance register data to daily entries
  const transformAttendanceData = (apiData: any, year: number, month: number): DailyAttendanceEntry[] => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const entries: DailyAttendanceEntry[] = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      // Check if we have data for this day from API
      const dayKey = String(day).padStart(2, '0');
      const apiDayData = apiData?.[dayKey] || apiData?.[day] || null;

      let status: DailyAttendanceEntry['status'] = 'no-response';
      let inTime = '';
      let outTime = '';

      if (isWeekend) {
        status = 'weekend';
      } else if (apiDayData) {
        if (apiDayData.status === 'P' || apiDayData.present || apiDayData === 'P') {
          status = 'present';
          inTime = apiDayData.inTime || '09:00 AM';
          outTime = apiDayData.outTime || '05:00 PM';
        } else if (apiDayData.status === 'A' || apiDayData.absent || apiDayData === 'A') {
          status = 'absent';
        } else if (apiDayData.status === 'L' || apiDayData.leave || apiDayData === 'L') {
          status = 'leave';
        } else if (apiDayData.status === 'H' || apiDayData === 'H') {
          status = 'holiday';
        }
      }

      entries.push({
        date: dateStr,
        day,
        dayOfWeek,
        status,
        inTime,
        outTime,
        workingHours: status === 'present' ? '8:00' : '',
        remarks: ''
      });
    }

    return entries;
  };

  // Load employee data from API
  const loadData = useCallback(async () => {
    if (!empId) return;

    setIsLoading(true);

    try {
      // Fetch employee profile (pass emptype for contractual)
      const emptypeParam = passedEmpType === 'contractual' ? 'contractual' : undefined;
      const profileResponse = await attendanceApi.getEmployeeProfile(empId, emptypeParam);

      if (profileResponse) {
        const transformedProfile = transformProfileData(profileResponse, empId, Number(kvId) || 1, passedKvInfo);
        setProfile(transformedProfile);
      } else if (passedEmployee) {
        // Use passed employee data if API fails
        setProfile(transformProfileData(passedEmployee, empId, Number(kvId) || 1, passedKvInfo));
      }

      // For monthly register, we'll use the staff attendance register API
      // Since there's no specific employee attendance register API, we'll generate from available data
      const year = viewYear % 100;
      const month = viewMonth;

      // Try to get attendance data - this may need backend support
      const response = await attendanceApi.getStaffAttendanceRegister(1, year, month, undefined, undefined);

      // Find this employee in the response
      const empData = response?.data?.find((e: any) =>
        String(e.empId) === String(empId) || String(e.empCode) === String(empId)
      );

      // Generate calendar entries
      const entries = transformAttendanceData(empData?.attendance || {}, viewYear, viewMonth);
      setMonthlyRegister(entries);

      // Calculate stats
      const workingDays = entries.filter(e => e.status !== 'weekend' && e.status !== 'holiday');
      const presentDays = entries.filter(e => e.status === 'present').length;
      const absentDays = entries.filter(e => e.status === 'absent').length;
      const leaveDays = entries.filter(e => e.status === 'leave').length;

      setStats({
        totalDays: workingDays.length,
        presentDays,
        absentDays,
        leaveDays,
        attendancePercent: workingDays.length > 0
          ? Math.round((presentDays / workingDays.length) * 100)
          : 0
      });

    } catch (err) {
      console.error('Error loading employee data:', err);

      // Fallback to passed data or defaults
      if (passedEmployee) {
        setProfile(transformProfileData(passedEmployee, empId, Number(kvId) || 1, passedKvInfo));
      }

      // Generate empty calendar
      const entries = transformAttendanceData({}, viewYear, viewMonth);
      setMonthlyRegister(entries);
      setStats({
        totalDays: 0,
        presentDays: 0,
        absentDays: 0,
        leaveDays: 0,
        attendancePercent: 0
      });
    } finally {
      setIsLoading(false);
    }
  }, [empId, kvId, viewYear, viewMonth, passedEmployee, passedKvInfo, passedEmpType]);

  // Load data on mount and when month changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Navigate month
  const handlePrevMonth = () => {
    if (viewMonth === 1) {
      setViewMonth(12);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewYear === currentYear && viewMonth >= currentMonth) return;
    if (viewMonth === 12) {
      setViewMonth(1);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // Calendar grid
  const calendarGrid = useMemo(() => {
    if (!monthlyRegister.length) return [];

    const firstDay = new Date(viewYear, viewMonth - 1, 1).getDay();
    const grid: (DailyAttendanceEntry | null)[] = [];

    // Add empty cells for days before first of month
    for (let i = 0; i < firstDay; i++) {
      grid.push(null);
    }

    // Add days
    monthlyRegister.forEach(entry => {
      grid.push(entry);
    });

    return grid;
  }, [monthlyRegister, viewYear, viewMonth]);

  // Get status color for calendar
  const getStatusColor = (status: DailyAttendanceEntry['status']) => {
    switch (status) {
      case 'present': return 'bg-green-500 text-white';
      case 'absent': return 'bg-red-500 text-white';
      case 'leave': return 'bg-blue-500 text-white';
      case 'holiday': return 'bg-purple-200 text-purple-700';
      case 'weekend': return 'bg-gray-100 text-gray-400';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(`/attendance/analytics/kv/${kvId}`);
    }
  };

  // Export attendance
  const handleExport = () => {
    if (!monthlyRegister.length) return;

    const headers = ['Date', 'Day', 'Status', 'In Time', 'Out Time', 'Working Hours', 'Remarks'];
    const rows = monthlyRegister.map(entry => [
      entry.date,
      entry.dayOfWeek,
      entry.status,
      entry.inTime || '',
      entry.outTime || '',
      entry.workingHours || '',
      entry.remarks || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${profile?.empName || 'employee'}-attendance-${viewYear}-${String(viewMonth).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl px-4 -pt-2 -sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#6A1786]">
                {profile?.empName || 'Loading...'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {profile?.designation} • ID: {empId}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExport}
              disabled={!monthlyRegister.length}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
            >
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex flex-wrap items-start gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#6A1786] to-[#8A37A6] flex items-center justify-center text-white text-3xl font-bold">
              {profile?.empName?.charAt(0) || '?'}
            </div>

            {/* Details */}
            <div className="flex-1 min-w-[250px]">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-gray-900">{profile?.empName}</h2>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${profile?.empType === 'regular'
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-orange-100 text-orange-700'
                  }`}>
                  {profile?.empType === 'regular' ? 'Regular' : 'Contractual'}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  <span>{profile?.designation || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span>{profile?.currentKvName || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{profile?.currentStateName || 'N/A'}</span>
                </div>
                {profile?.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{profile.email}</span>
                  </div>
                )}
                {profile?.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{profile.phone}</span>
                  </div>
                )}
                {profile?.dateOfJoining && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Joined: {profile.dateOfJoining}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            {stats && (
              <div className="flex flex-wrap gap-3">
                <div className="px-4 py-3 bg-green-50 rounded-xl text-center min-w-[90px]">
                  <UserCheck className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-green-700">{stats.presentDays}</p>
                  <p className="text-xs text-green-600">Present</p>
                </div>
                <div className="px-4 py-3 bg-red-50 rounded-xl text-center min-w-[90px]">
                  <UserX className="w-5 h-5 text-red-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-red-700">{stats.absentDays}</p>
                  <p className="text-xs text-red-600">Absent</p>
                </div>
                <div className="px-4 py-3 bg-blue-50 rounded-xl text-center min-w-[90px]">
                  <Calendar className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-blue-700">{stats.leaveDays}</p>
                  <p className="text-xs text-blue-600">Leave</p>
                </div>
                <div className="px-4 py-3 bg-purple-50 rounded-xl text-center min-w-[90px]">
                  <Award className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-purple-700">{stats.attendancePercent}%</p>
                  <p className="text-xs text-purple-600">Attendance</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Calendar View */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Monthly Attendance</h3>
            <div className="flex items-center gap-4">
              <button
                onClick={handlePrevMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <span className="text-lg font-medium text-gray-900 min-w-[150px] text-center">
                {MONTHS[viewMonth - 1]} {viewYear}
              </span>
              <button
                onClick={handleNextMonth}
                disabled={viewYear === currentYear && viewMonth >= currentMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-green-500"></span>
              <span className="text-gray-600">Present</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-red-500"></span>
              <span className="text-gray-600">Absent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-blue-500"></span>
              <span className="text-gray-600">Leave</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-purple-200"></span>
              <span className="text-gray-600">Holiday</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-gray-100"></span>
              <span className="text-gray-600">Weekend</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-yellow-100"></span>
              <span className="text-gray-600">No Response</span>
            </div>
          </div>

          {/* Calendar Grid */}
          {isLoading ? (
            <div className="animate-pulse">
              <div className="grid grid-cols-7 gap-2">
                {[...Array(35)].map((_, idx) => (
                  <div key={idx} className="aspect-square bg-gray-100 rounded-lg"></div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {DAYS_SHORT.map((day, idx) => (
                  <div key={idx} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar */}
              <div className="grid grid-cols-7 gap-2">
                {calendarGrid.map((entry, idx) => (
                  <div key={idx} className="aspect-square">
                    {entry ? (
                      <div
                        className={`w-full h-full rounded-lg flex flex-col items-center justify-center ${getStatusColor(entry.status)} cursor-pointer hover:opacity-80 transition-opacity`}
                        title={`${entry.date}: ${entry.status}${entry.inTime ? ` | In: ${entry.inTime}` : ''}${entry.outTime ? ` | Out: ${entry.outTime}` : ''}`}
                      >
                        <span className="text-lg font-medium">{entry.day}</span>
                        {entry.inTime && (
                          <span className="text-[10px] opacity-75">{entry.inTime}</span>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full rounded-lg bg-transparent"></div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Attendance Register Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Attendance Register</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Day</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">In Time</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Out Time</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Working Hours</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {monthlyRegister.map((entry) => (
                  <tr key={entry.date} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{entry.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.dayOfWeek}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${entry.status === 'present' ? 'bg-green-100 text-green-700' :
                        entry.status === 'absent' ? 'bg-red-100 text-red-700' :
                          entry.status === 'leave' ? 'bg-blue-100 text-blue-700' :
                            entry.status === 'holiday' ? 'bg-purple-100 text-purple-700' :
                              entry.status === 'weekend' ? 'bg-gray-100 text-gray-500' :
                                'bg-yellow-100 text-yellow-700'
                        }`}>
                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">
                      {entry.inTime || '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">
                      {entry.outTime || '-'}
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">
                      {entry.workingHours || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {entry.remarks || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EmployeeAttendanceDetail;
